import type { CSSProperties, ReactNode, Ref } from 'react'
import { useRef, useState } from 'react'
import { useEvent } from 'react-use-event-hook'
import type { AnimationDefinition } from 'motion/react'
import { motion } from 'motion/react'

import { resolveDurationMs } from './resolve-duration'

/** Material standard easing curve — the curve MUI's Grow animated with. */
const ANIMATION_EASE: [number, number, number, number] = [0.4, 0, 0.2, 1]
/** Scale the content grows from while entering. Matches MUI Grow's starting scale. */
const EXIT_SCALE = 0.75

/** Motion variant label for the shown pose. Used as a variant key and an animation target. */
const ENTERED = 'entered'
/** Motion variant label for the hidden pose. Used as a variant key and an animation target. */
const EXITED = 'exited'

/** Transition duration(s) in ms — the react-transition-group `timeout` shape. */
export type GrowTimeout = number | { appear?: number; enter?: number; exit?: number }

export type GrowProps = {
  /** Show the component; flipping this triggers the enter or exit transition. */
  in?: boolean
  /** Animate on initial mount when mounted already `in`. Defaults to `true`. */
  appear?: boolean
  /** Remove children from the DOM once the exit transition finishes. */
  unmountOnExit?: boolean
  /** Transition duration(s) in ms. A number applies to both directions. */
  timeout?: GrowTimeout
  /** Inline styles forwarded to the animating wrapper (e.g. a `transformOrigin`). */
  style?: CSSProperties
  /** Extra classes for the animating wrapper element. */
  className?: string
  /** Content to grow in and out. */
  children?: ReactNode
  /** Fired before the enter transition starts. Receives the wrapper node. */
  onEnter?: (node: HTMLElement, isAppearing: boolean) => void
  /** Fired as the enter transition starts. */
  onEntering?: (node: HTMLElement, isAppearing: boolean) => void
  /** Fired after the enter transition finishes. */
  onEntered?: (node: HTMLElement, isAppearing: boolean) => void
  /** Fired before the exit transition starts. */
  onExit?: (node: HTMLElement) => void
  /** Fired as the exit transition starts. */
  onExiting?: (node: HTMLElement) => void
  /** Fired after the exit transition finishes. Parents (Popper, notistack) unmount on this. */
  onExited?: (node: HTMLElement) => void
  /** Ref to the animating wrapper element. */
  ref?: Ref<HTMLDivElement>
}

const variants = {
  [ENTERED]: { opacity: 1, scale: 1 },
  [EXITED]: { opacity: 0, scale: EXIT_SCALE },
}

/**
 * Grow — scales and fades its child in and out.
 *
 * A `motion`-based replacement for MUI's `Grow` that preserves the
 * react-transition-group `TransitionProps` contract (`in`, `appear`, `timeout`,
 * `style`, and the six `onEnter*`/`onExit*` lifecycle callbacks). Preserving that
 * contract is what keeps it a drop-in for MUI `Popper`'s `transition` render-prop
 * and notistack's `TransitionComponent` — both keep the child mounted and unmount
 * it only after `onExited` fires.
 */
export const Grow = ({
  in: inProp = false,
  appear = true,
  unmountOnExit = false,
  timeout,
  style,
  className,
  children,
  onEnter,
  onEntering,
  onEntered,
  onExit,
  onExiting,
  onExited,
  ref,
}: GrowProps) => {
  // Sanctioned "derive state during render": flip `mounted` true the instant `in`
  // turns on, so the child mounts in its EXITED pose and can animate in.
  const [mounted, setMounted] = useState(inProp || !unmountOnExit)
  if (inProp && !mounted) {
    setMounted(true)
  }

  // Seeds RTG's isAppearing: with appear=false the element mounts already-entered,
  // so no later enter counts as an appearance.
  const hasEnteredRef = useRef(!appear)
  // The wrapper DOM node, forwarded to lifecycle callbacks (notistack reads it).
  const nodeRef = useRef<HTMLDivElement>(null)

  const setNode = useEvent((node: HTMLDivElement | null) => {
    nodeRef.current = node
    if (typeof ref === 'function') {
      ref(node)
    } else if (ref) {
      ref.current = node
    }
  })

  const transition = {
    duration: resolveDurationMs(timeout, inProp ? 'enter' : 'exit') / 1000,
    ease: ANIMATION_EASE,
  }

  const handleAnimationStart = (definition: AnimationDefinition) => {
    const node = nodeRef.current
    if (!node) {
      return
    }
    if (definition === ENTERED) {
      const isAppearing = !hasEnteredRef.current
      onEnter?.(node, isAppearing)
      onEntering?.(node, isAppearing)
    } else if (definition === EXITED) {
      onExit?.(node)
      onExiting?.(node)
    }
  }

  const handleAnimationComplete = (definition: AnimationDefinition) => {
    const node = nodeRef.current
    if (definition === ENTERED) {
      if (node) {
        onEntered?.(node, !hasEnteredRef.current)
      }
      hasEnteredRef.current = true
    } else if (definition === EXITED) {
      if (node) {
        onExited?.(node)
      }
      if (unmountOnExit) {
        setMounted(false)
      }
    }
  }

  if (!mounted) {
    return null
  }

  return (
    <motion.div
      ref={setNode}
      animate={inProp ? ENTERED : EXITED}
      className={className}
      initial={appear ? EXITED : false}
      style={style}
      transition={transition}
      variants={variants}
      onAnimationComplete={handleAnimationComplete}
      onAnimationStart={handleAnimationStart}
    >
      {children}
    </motion.div>
  )
}
